import { difference, intersection } from 'lodash';
import { Dispatch } from 'redux';

import { TDispatchProps } from '../../typings/utils';
import {
    DEFAULT_FETCH_LIMIT,
    DEFAULT_GALLERY_FILES_REQUEST,
    GALLERY_DEFAULT_FETCH_FILTER_NAME,
    GALLERY_DEFAULT_FETCH_OFFSET,
    GALLERY_DEFAULT_FETCH_ORDER,
    GALLERY_DEFAULT_SEMANTIC_TEXT_FILTER,
    ResourceType,
    SharedData
} from '../common/typings';
import { CommonOptionGroupName, CreativeOptionsGroupValue, CreativeOptionsValue } from '../CreativeOptions/typings';
import { FileEditorOptions } from '../FileEditor/typings';

import {
    applyItem,
    closeGalleryPopup,
    editItemName,
    fetchInitialData,
    GalleryAction,
    removeItem,
    selectTab
} from './actions';
import {
    CommonGalleryProps,
    GalleriesRequestData,
    GalleriesState,
    GalleryAudioCollectionItem,
    GalleryCollectionItem,
    GalleryCommonPaneFilters,
    GalleryCommonPaneState,
    GalleryDataForPane,
    GalleryIdeasPaneData,
    GalleryIdeasPaneFilters,
    GalleryImageCollectionItem,
    GalleryPaneFilters,
    GalleryPaneTypes,
    GalleryRecentPaneData,
    GalleryRecentPaneFilters,
    GalleryResourceData,
    GalleryStockPaneData,
    GalleryStockPaneFilters,
    GalleryVideoCollectionItem
} from './typings';

export const getGalleriesInitialState = (): GalleriesState => {
    return {
        currentResourceType: ResourceType.image,
        isPopupOpened: false,
        resources: {
            [ResourceType.image]: getDefaultGalleryResourceData<GalleryImageCollectionItem>(),
            [ResourceType.audio]: getDefaultGalleryResourceData<GalleryAudioCollectionItem>(),
            [ResourceType.video]: getDefaultGalleryResourceData<GalleryVideoCollectionItem>()
        }
    };
};

export const getStockPaneDefaultData = <T>(): GalleryStockPaneData<T> => {
    return {
        ...getCommonPaneDefaultData(),
        filters: getStockPaneDefaultFilters()
    };
};

export const getRecentPaneDefaultData = <T>(): GalleryRecentPaneData<T> => {
    return {
        ...getCommonPaneDefaultData(),
        filters: getRecentPaneDefaultFilters()
    };
};

export const getIdeasPaneDefaultData = <T>(): GalleryIdeasPaneData<T> => {
    return {
        ...getCommonPaneDefaultData(),
        filters: getIdeasPaneDefaultFilters()
    };
};

export const getDefaultGalleryResourceData = <T>(): GalleryResourceData<T> => {
    return {
        isBusy: false,
        currentPaneType: GalleryPaneTypes.recent,
        enabledGalleryTypes: [GalleryPaneTypes.recent],
        allGalleryTypes: [GalleryPaneTypes.recent],
        panes: {
            [GalleryPaneTypes.recent]: getRecentPaneDefaultData<T>(),
            [GalleryPaneTypes.stock]: getStockPaneDefaultData<T>(),
            [GalleryPaneTypes.idea]: getIdeasPaneDefaultData<T>()
        }
    };
};

export const getDefaultGalleryDataForPane = <T>(): GalleryDataForPane<T> => {
    return {
        collection: Array<T>(),
        total: 0
    };
};

const getCommonPaneDefaultData = <T>(): GalleryCommonPaneState<T> => {
    return {
        isAllItemsLoading: false,
        isMoreItemsLoading: false,
        isFetched: false,
        data: getDefaultGalleryDataForPane<T>()
    };
};

const getCommonPaneDefaultFilters = (): GalleryCommonPaneFilters => {
    return {
        limit: DEFAULT_FETCH_LIMIT,
        offset: GALLERY_DEFAULT_FETCH_OFFSET
    };
};

export const getStockPaneDefaultFilters = (): GalleryStockPaneFilters => {
    return {
        semanticText: GALLERY_DEFAULT_SEMANTIC_TEXT_FILTER,
        selectedGalleryCategories: Array<string>(),
        ...getCommonPaneDefaultFilters()
    };
};

export const getRecentPaneDefaultFilters = (): GalleryRecentPaneFilters => {
    return {
        recentOrder: GALLERY_DEFAULT_FETCH_ORDER,
        recentNameFilter: GALLERY_DEFAULT_FETCH_FILTER_NAME,
        ...getCommonPaneDefaultFilters()
    };
};

export const getIdeasPaneDefaultFilters = (): GalleryIdeasPaneFilters => {
    return getCommonPaneDefaultFilters();
};

type CommonGalleryDispatchProps = TDispatchProps<CommonGalleryProps>;

// todo если кто-то знает лучшее место для этого хэлпера - переместите
export const getCommonGalleryDispatchToProps = (dispatch: Dispatch<GalleryAction>): CommonGalleryDispatchProps => {
    return {
        removeItem: (collectionIndex: number, itemName?: string): void => {
            dispatch(removeItem(collectionIndex, itemName));
        },
        editItemName: (collectionIndex: number, name: string): void => {
            dispatch(editItemName(collectionIndex, name));
        },
        applyItem: (collectionIndex: number): void => {
            dispatch(applyItem(collectionIndex));
        },
        selectTab: (currentPaneType: GalleryPaneTypes): void => {
            dispatch(selectTab(currentPaneType));
        },
        closeGalleryPopup: (): void => {
            dispatch(closeGalleryPopup());
        },
        fetchInitialData: (resourceType: ResourceType): void => {
            dispatch(fetchInitialData(resourceType));
        }
    };
};

export const getRequestFiltersFromGalleryFilters = (
    type: GalleryPaneTypes,
    filters: GalleryPaneFilters,
    isVideo?: boolean
): GalleriesRequestData => {
    const requestData = {
        ...DEFAULT_GALLERY_FILES_REQUEST,
        offset: filters.offset,
        limit: filters.limit
    };

    switch (type) {
        case GalleryPaneTypes.recent:
            const recentFilters = filters as GalleryRecentPaneFilters;
            requestData.name = recentFilters.recentNameFilter;
            requestData.sort_order = recentFilters.recentOrder;
            break;

        case GalleryPaneTypes.stock:
            const stockFilters = filters as GalleryStockPaneFilters;
            requestData.semantic_search = stockFilters.semanticText;
            if (isVideo) {
                requestData.groups = stockFilters.selectedGalleryCategories;
            } else {
                requestData.categories = stockFilters.selectedGalleryCategories;
            }
            break;

        default:
            break;
    }

    return requestData;
};

// Типы галлерей которые грузятся сразу при инициализации страницы редактирования креатива
const AUTO_LOAD_GALLERY_TYPES = [GalleryPaneTypes.recent, GalleryPaneTypes.idea];

export const getAutoLoadGalleryTypes = (enabledGalleryTypes: GalleryPaneTypes[]): GalleryPaneTypes[] => {
    return intersection(enabledGalleryTypes, AUTO_LOAD_GALLERY_TYPES);
};

// Типы галлерей, которые грузятся только если пытаются открыть галлерею изображений
// Пока типы из данного списка не подгрузились, в галлерее показывается троббер
export const getLoadOnOpenGalleryTypes = (enabledGalleryTypes: GalleryPaneTypes[]): GalleryPaneTypes[] => {
    return difference(enabledGalleryTypes, AUTO_LOAD_GALLERY_TYPES);
};

export const galleryTypesNeedToFetchOnOpen = (
    gallery: GalleryResourceData<GalleryCollectionItem>
): GalleryPaneTypes[] => {
    const loadOnOpenGalleryTypes = getLoadOnOpenGalleryTypes(gallery.enabledGalleryTypes);
    return loadOnOpenGalleryTypes.reduce((acc: GalleryPaneTypes[], type: GalleryPaneTypes) => {
        if (!gallery.panes[type].isFetched) {
            acc.push(type);
        }

        return acc;
    }, []);
};

export const isAutoLoadGalleryType = (type: GalleryPaneTypes): boolean => {
    return AUTO_LOAD_GALLERY_TYPES.includes(type);
};

export const hasPaneTypeSources = (
    enabledGalleryTypes: GalleryPaneTypes[],
    collection: GalleryCollectionItem[],
    pane: GalleryPaneTypes
): boolean => {
    const supportsGalleryType = enabledGalleryTypes.includes(pane);

    if (!supportsGalleryType) {
        return false;
    }

    return collection.length > 0;
};

export const resetGalleryFetchParams = (filters: GalleryPaneFilters): GalleryPaneFilters => {
    return {
        ...filters,
        offset: GALLERY_DEFAULT_FETCH_OFFSET,
        limit: DEFAULT_FETCH_LIMIT
    };
};

export const getGalleryTypesForResource = (
    resourceType: ResourceType,
    sharedData: SharedData,
    isIdea?: boolean
): GalleryPaneTypes[] => {
    const types: GalleryPaneTypes[] = [];

    // у аутдор и индор библиотеки не поддерживаются
    if (sharedData.cpm_outdoor || sharedData.cpm_indoor) {
        return [];
    }

    if (isIdea) {
        types.push(GalleryPaneTypes.idea);
    }

    switch (resourceType) {
        case ResourceType.image:
            // для картинок в аудио библиотеки не поддерживаются
            if (sharedData.cpm_audio) {
                return types;
            }
            // для пэкшотов на видео и картинок в аудио вкладка STOCK недоступна
            if (!sharedData.cpm) {
                types.push(GalleryPaneTypes.stock);
            }

            types.push(GalleryPaneTypes.recent);

            return types;
        case ResourceType.video:
            types.push(GalleryPaneTypes.stock, GalleryPaneTypes.recent);

            return types;
        case ResourceType.audio:
            // для аудио-креативов вкладка STOCK недоступна
            if (!sharedData.cpm_audio) {
                types.push(GalleryPaneTypes.stock);
            }

            types.push(GalleryPaneTypes.recent);

            return types;
        default:
            throw `Resource type ${resourceType} is not supported`;
    }
};

const VIDEO_OPTION_NAME = 'video_id';
const VIDEO_OPTION_TYPE = 'addition';

export function getCreativeOptionsWithVideoGalleryItem(
    options: CreativeOptionsValue,
    videoId: string,
    editorOptions?: FileEditorOptions
): CreativeOptionsValue {
    const groupName = editorOptions ? editorOptions.groupName : VIDEO_OPTION_TYPE;
    const name = editorOptions ? editorOptions.name : VIDEO_OPTION_NAME;

    return options.map((group: CreativeOptionsGroupValue) => {
        switch (group.type) {
            case CommonOptionGroupName.image:
            case CommonOptionGroupName.logo:
                return group;

            default:
                break;
        }

        if (group.type !== groupName) {
            return group;
        }

        return {
            ...group,
            options: {
                ...group.options,
                [name]: videoId
            }
        };
    });
}

export function needToChangeOverlayColor(galleryItem: GalleryCollectionItem, sharedData: SharedData): boolean {
    // только у файлов библиотеки есть нормальный overlayColor
    // для загруженных пользователем он равен #000000
    return (
        Boolean(galleryItem.stock_file_id) &&
        // у аутдор/индор/аудио нет опций,
        // которые бы зависели от цвета видео
        !sharedData.cpm_outdoor &&
        !sharedData.cpm_indoor &&
        !sharedData.cpm_audio
    );
}

export const needToReloadRecent = (resourceType: ResourceType, sharedData: SharedData, isIdea?: boolean): boolean => {
    const enabledTypes = getGalleryTypesForResource(resourceType, sharedData, isIdea);

    return enabledTypes.indexOf(GalleryPaneTypes.recent) !== -1;
};
